import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PairedLocatedVelavuDevice } from "velavu-js-api";
import Divider from "../../../../elements/divider";
import useAssetFiltering from "../../../../helper/assets-filter-hook";
import { NoAssetsIcon } from "../../../../img/icons/icons";
import AssetListHeader from "./asset-list-header";
import AssetListFilter from "./asset-list-filter";
import * as styles from "./asset-list.module.scss";
import AssetListItem from "./asset-listitem";
import FloorFilter from "../../../../data/floor-filter";

interface AssetListProps {
	devices: PairedLocatedVelavuDevice[];
	onSelectDevice: (deviceID: string) => void;
	onHoverDevice: (deviceID: string | undefined) => void;

	selectedFloorNum: number | undefined;
	onChangeFloorFilter: (filter: FloorFilter) => void;
	showFloorFilter: boolean;
}

function NoResults() {
	const { t } = useTranslation();

	return (
		<div className={styles.emptyContent}>
			<NoAssetsIcon />
			<span className={styles.heading}>
				{t("dashboard.asset_list.no_results")}
			</span>
			<span className={styles.info}>
				{t("dashboard.asset_list.no_results_hint")}
			</span>
		</div>
	);
}

export default function AssetList(props: AssetListProps) {
	const {
		devices: filteredDevices,
		filterCategory,
		filterStatus,
		isFiltering,
		searchText,
		setFilterCategory,
		setFilterStatus,
		setSearchText,
		showFilterMenu,
		toggleFilterMenu,
	} = useAssetFiltering(props.devices);

	const {
		onSelectDevice: propsOnSelectDevice,
		onHoverDevice: propsOnHoverDevice,
	} = props;

	const result = useMemo(() => {
		if (filteredDevices.length === 0) {
			return <NoResults />;
		}

		return filteredDevices.map((device) => (
			<AssetListItem
				key={device.asset.id}
				className={styles.item}
				asset={device.asset}
				onClick={() => propsOnSelectDevice(device.id)}
				onMouseEnter={() => propsOnHoverDevice(device.id)}
				onMouseLeave={() => propsOnHoverDevice(undefined)}
			/>
		));
	}, [filteredDevices, propsOnSelectDevice, propsOnHoverDevice]);

	return (
		<React.Fragment>
			<AssetListHeader
				searchText={searchText}
				setSearchText={setSearchText}
				selectedFloorNum={props.selectedFloorNum}
				onChangeFloorFilter={props.onChangeFloorFilter}
				showFloorFilter={props.showFloorFilter}
				isFiltering={isFiltering}
				showFilterMenu={showFilterMenu}
				onToggleFilterMenu={toggleFilterMenu}
			/>
			<Divider />
			{showFilterMenu && (
				<>
					<AssetListFilter
						filterCategory={filterCategory}
						selectCategory={setFilterCategory}
						filterStatus={filterStatus}
						selectStatus={setFilterStatus}
					/>
					<Divider />
				</>
			)}
			<div className={styles.list}>{result}</div>
		</React.Fragment>
	);
}
